'use client';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Result { rk: string; nm: string; ds: string; lvl?: string; mt: string; bar: number }

interface Scenario {
  role: 'COMPRADOR' | 'PROVEEDOR';
  q: string;
  meta: { conf: string; ms: string; n: string };
  results: Result[];
}

const SCENARIOS: Record<string, Scenario> = {
  comprador1: {
    role: 'COMPRADOR',
    q: 'necesito proveedor de carpintería metálica con experiencia en hospitales y verificado',
    meta: { conf: 'confianza 0.92', ms: 'parse 380ms · match 1.1s', n: '18 candidatos' },
    results: [
      { rk: '01', nm: 'Metalmec Andina S.A.C.', ds: 'fachada · carpintería · 14 obras hospitalarias', lvl: 'N3 · Verificado ARKU', mt: '94%', bar: 94 },
      { rk: '02', nm: 'CIM Estructuras', ds: 'estructura metálica · 6 hospitales · Lima/Arequipa', lvl: 'N2 · Documentado', mt: '88%', bar: 88 },
      { rk: '03', nm: 'Promet SA', ds: 'carpintería arquitectónica · retail + salud', lvl: 'N3 · Verificado ARKU', mt: '83%', bar: 83 },
      { rk: '04', nm: 'Estructuras del Sur', ds: 'metálica pesada · 3 hospitales regionales', lvl: 'N1 · Declarado', mt: '76%', bar: 76 },
    ],
  },
  comprador2: {
    role: 'COMPRADOR',
    q: 'supervisión de obra retail con experiencia en consorcios mall plaza',
    meta: { conf: 'confianza 0.88', ms: 'parse 410ms · match 940ms', n: '11 candidatos' },
    results: [
      { rk: '01', nm: 'Vértice Consultores AEC', ds: 'supervisión retail · 9 mall plaza + paseo', lvl: 'N3 · Verificado ARKU', mt: '91%', bar: 91 },
      { rk: '02', nm: 'Cesel Ingeniería', ds: 'supervisión obra · retail + oficinas', lvl: 'N4 · Tercero (SGS)', mt: '89%', bar: 89 },
      { rk: '03', nm: 'JNR Consultores', ds: 'consorcios retail · 6 proyectos en curso', lvl: 'N2 · Documentado', mt: '82%', bar: 82 },
    ],
  },
  proveedor: {
    role: 'PROVEEDOR',
    q: 'oportunidades de fachada y muro cortina > S/3M en Lima en los últimos 30 días',
    meta: { conf: 'confianza 0.94', ms: 'parse 290ms · match 720ms', n: '7 oportunidades' },
    results: [
      { rk: '01', nm: 'Torre San Isidro · muro cortina', ds: 'privado · S/ 8.2M · cierre en 12d', lvl: 'Requerimiento', mt: '92%', bar: 92 },
      { rk: '02', nm: 'Hospital Regional · fachada', ds: 'SEACE · S/ 4.8M · cierre en 18d', lvl: 'Licitación', mt: '90%', bar: 90 },
      { rk: '03', nm: 'Centro empresarial Surco', ds: 'inmobiliaria · anteproyecto · capex est. S/ 6M', lvl: 'Señal', mt: '78%', bar: 78 },
    ],
  },
};

const TABS = [
  { key: 'comprador1', label: 'Comprador · carpintería metálica' },
  { key: 'comprador2', label: 'Comprador · supervisión retail' },
  { key: 'proveedor', label: 'Proveedor · watchlist de fachadas' },
];

export function Consola() {
  const [tab, setTab] = useState<string>('comprador1');
  const scenario = SCENARIOS[tab];
  const [typed, setTyped] = useState('');
  const [showResults, setShowResults] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reduce = useMemo(() => typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);

  useEffect(() => {
    setTyped('');
    setShowResults(false);
    if (timer.current) clearTimeout(timer.current);

    if (reduce) {
      setTyped(scenario.q);
      setShowResults(true);
      return;
    }

    const full = scenario.q;
    let i = 0;
    const tick = () => {
      i++;
      setTyped(full.slice(0, i));
      if (i < full.length) {
        timer.current = setTimeout(tick, 28 + Math.random() * 22);
      } else {
        timer.current = setTimeout(() => setShowResults(true), 350);
      }
    };
    timer.current = setTimeout(tick, 200);

    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [tab, scenario.q, reduce]);

  return (
    <section className="section" id="consola">
      <div className="wrap">
        <div className="sec-index">
          <span className="n">04</span>
          <span className="ln" />
          <span className="t">Inteligencia · lenguaje natural</span>
        </div>
        <h2 className="display">
          Pregunta como hablas. <em>Responde con la red.</em>
        </h2>
        <p className="lede">
          Sin filtros ni comandos. El mismo motor sirve a los dos lados: descubre oportunidades
          para el que vende y proveedores calificados para el que compra.
        </p>

        <div className="console">
          <div className="console-bar">
            <span className="dots"><i /><i /><i /></span>
            <span className="path">arku · consulta · <span>{scenario.role}</span></span>
            <span className="mode">
              <span className="on">Lenguaje natural</span>
              <span>Match por afinidad</span>
            </span>
          </div>
          <div className="console-body">
            <div className="console-prompt">
              <span className="chev">›</span>
              <span className="q">{typed}</span>
              <span className="cursor" />
            </div>
            <div className={`console-meta${showResults ? ' show' : ''}`}>
              <span><b>{scenario.meta.conf}</b></span>
              <span>{scenario.meta.ms}</span>
              <span>{scenario.meta.n}</span>
            </div>
            <div className="results">
              {scenario.results.map((r, i) => (
                <div className={`res${showResults ? ' in' : ''}`} key={`${tab}-${i}`} style={{ transitionDelay: `${i * 80}ms` }}>
                  <div className="rk">{r.rk}</div>
                  <div className="co">
                    <div className="nm">{r.nm}</div>
                    <div className="ds">{r.ds}</div>
                  </div>
                  {r.lvl && <div className="lvl">{r.lvl}</div>}
                  <div className="mt">
                    {r.mt}
                    <span className="bar"><i style={{ width: `${r.bar}%` }} /></span>
                  </div>
                </div>
              ))}
            </div>
            <div className="console-tabs">
              {TABS.map(t => (
                <button
                  key={t.key}
                  type="button"
                  className={t.key === tab ? 'on' : ''}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
